import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { TransactionHistory } from 'shared/transactionHistory';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AccountScopedApiProvider from './AccountScopedApiProvider';
import useAccountTransactionsHostProps from './useAccountTransactionsHostProps';
import styles from './TransactionsTab.module.scss';

const cn = withStyles(styles);

/**
 * The account page's Transactions tab (spec 5.6.7, ID-13369): the shared Transaction History, scoped to
 * this account by its own bearer. Chrome-less by design — the tab card is the container, and the
 * heading rides on the list's own control row. The same tab serves the sub-account page.
 */
export default function TransactionsTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer, hasSettled } = useAccountBearer(accountId);
  const hostProps = useAccountTransactionsHostProps(accountId);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // On a deep link the token is looked up after mount; the list must not fire its first fetch — which
  // it does on mount — against the partner session in the meantime.
  if (!bearer && !hasSettled) return <Skeleton height={320} data-testid="transactionsTabLoading" />;

  // The lookup has answered and there is no token to scope the list with: nothing it renders would
  // belong to this account, so the tab says so instead of holding its skeleton for good.
  if (!bearer) {
    return (
      <BodyText data-testid="transactionsTabUnavailable">
        {t('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    // The list's table is wider than the tab card; it scrolls sideways inside the tab instead of
    // spilling past the card's edge.
    <div className={cn('scroll')}>
      <AccountScopedApiProvider bearer={bearer} readOnly={isRoleViewOnly}>
        <TransactionHistory {...hostProps} title={t('transactions', { defaultValue: 'Transactions' })} />
      </AccountScopedApiProvider>
    </div>
  );
}
